import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * `<prev-gen-callout>` — keeps the Prusa "previous generation" callout in sync
 * with the selected variant.
 *
 * The headline is fixed ("Want it sooner?"), but the detail line under it is
 * variant-dependent (CORE One+ Gen 2: the previous-gen Assembled is 10% off, the
 * previous-gen Kit is not discounted and instead ships two free spools), and the
 * shop links move with it. All of it is resolved server-side from
 * `selected_or_first_available_variant` in
 * snippets/prusa-prev-gen-callout.liquid, so first paint — including a
 * `?variant=` deep link — is correct with no JS at all.
 *
 * On a variant change we take the freshly rendered callout out of the section
 * HTML the variant picker already fetched and morph this element to match, the
 * same approach as `assets/pdp-stock-pill.js` and `assets/product-inventory.js`.
 * No full page reload, and no second copy of the offer rules in JS to drift out
 * of sync with the Liquid ones.
 */
class PrevGenCallout extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateCallout);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateCallout);
  }

  /**
   * Updates the callout to reflect the newly selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateCallout = (event) => {
    // Combined listing: a different product was loaded, so adopt its id to keep
    // matching. Otherwise ignore updates dispatched for a different product.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newCallout = event.detail.data.html.querySelector('prev-gen-callout');

    // The rules table always resolves a row once the handle matches, so a
    // missing callout means the new product simply is not gated in — hide
    // rather than leave a stale offer on screen.
    if (!newCallout) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    morph(this, newCallout, { childrenOnly: true });
  };
}

if (!customElements.get('prev-gen-callout')) {
  customElements.define('prev-gen-callout', PrevGenCallout);
}
